import { createFileRoute } from "@tanstack/react-router";
import { localProductionSection } from "@/lib/seo/local-production";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { breadcrumbs, faqPage, serviceSchema, speakable, aeoGeo } from "@/lib/seo/schema";

const FAQ: LandingFAQ[] = [
  {
    q: "Vad kostar eventfilm i Göteborg?",
    a: "En dag med en fotograf och klipp ligger på 30 000–60 000 kr. Konferens med multikamera, scenljud och intervjuer: 80 000–250 000 kr. Alla priser exklusive moms.",
  },
  {
    q: "Har ni team på plats i Göteborg?",
    a: "Ja, vi producerar löpande i Göteborg och Västsverige. Det håller restid och riggtid nere och gör kort varsel möjligt.",
  },
  {
    q: "Kan vi få material samma dag?",
    a: "Ja. Vi klipper på plats och levererar social-cut under eventdagen. Huvudfilm och intervjuklipp kommer inom 1–2 veckor.",
  },
  {
    q: "Filmar ni mässor på Svenska Mässan?",
    a: "Vi arbetar i Göteborgs mässmiljöer och konferenslokaler. Vi gör alltid en teknisk recce av ljus, ljud och kameraplaceringar innan eventdagen.",
  },
  {
    q: "Hur många kameror behövs?",
    a: "Mindre kundträff klarar sig med en kamera. Har eventet scen med talare rekommenderar vi två till tre kameror så att klippningen blir användbar och ljudet konsekvent.",
  },
];

export const Route = createFileRoute("/eventfilm_/goteborg")({
  head: () => ({
    meta: [
      { title: "Eventfilm Göteborg — same-day leverans och pris | Fenny" },
      {
        name: "description",
        content:
          "Eventfilm i Göteborg för konferenser, mässor och lanseringar. Same-day social-cut, huvudfilm inom 1–2 veckor. Pris 30 000–250 000 kr beroende på omfattning.",
      },
      { property: "og:title", content: "Eventfilm Göteborg — Fenny" },
      {
        property: "og:description",
        content:
          "Eventproduktion i Göteborg med same-day leverans, multikamera och talarintervjuer.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/eventfilm/goteborg" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/eventfilm/goteborg" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/eventfilm/goteborg", name: "Eventfilm Göteborg — same-day leverans och pris | Fenny", description: "Eventfilm i Göteborg för konferenser, mässor och lanseringar. Same-day social-cut, huvudfilm inom 1–2 veckor. Pris 30 000–250 000 kr beroende på omfattning."})) },
      { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "Eventfilm Göteborg",
            path: "/eventfilm/goteborg",
            serviceType: "Eventfilm-produktion",
            description:
              "Eventfilm i Göteborg för konferenser, mässor, lanseringar och kundträffar — same-day social-cut och huvudfilm.",
            priceRange: "30 000–250 000 kr",
            areaServed: ["Göteborg"],
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Eventfilm", path: "/eventfilm" },
            { name: "Göteborg", path: "/eventfilm/goteborg" },
          ]),
        ),
      },
    ],
  }),
  component: EventfilmGoteborgPage,
});

function EventfilmGoteborgPage() {
  return (
    <LandingPage
      eyebrow="Eventfilm Göteborg"
      title={<>Eventfilm i Göteborg — innehåll som räcker hela kvartalet.</>}
      lede="Fenny filmar konferenser, mässor, lanseringar och kundträffar i Göteborg och Västsverige. Social-cut levereras samma dag, huvudfilm och talarintervjuer inom 1–2 veckor. En eventdag ger material för månader av kommunikation."
      tldr={
        <p>
          Eventfilm i Göteborg kostar <strong>30 000–60 000 kr</strong> för en
          dag med en fotograf och klipp, och{" "}
          <strong>80 000–250 000 kr</strong> för multikamera med scenljud,
          intervjuer och versionering. Same-day social-cut ingår.
        </p>
      }
      ctaPrimary={{ label: "Boka för ert event", to: "/kontakt" }}
      ctaSecondary={{ label: "Se case", to: "/case" }}
      trackingId="eventfilm-goteborg"
      bullets={[
        { title: "Same-day social", body: "Färdiga reels medan eventet pågår." },
        { title: "Västsverige", body: "Vi producerar löpande i Göteborg — kort varsel går ofta att lösa." },
        { title: "Scenljud direkt", body: "Vi kopplar mot husets ljudbord för användbart talarljud." },
      ]}
      pricing={{
        heading: "Vad kostar eventfilm i Göteborg?",
        intro: "Riktpriser exklusive moms. Exakt pris sätts efter agenda och lokal.",
        rows: [
          { format: "Kundträff / mindre event", scope: "1 fotograf, 1 dag, social-cut + highlight", price: "30 000–60 000 kr", lead: "same-day + 1 vecka" },
          { format: "Konferens med scen", scope: "2 kameror, scenljud, talarintervjuer", price: "80 000–150 000 kr", lead: "same-day + 2 veckor" },
          { format: "Mässa / lansering", scope: "3 kameror, producent, B-roll-bibliotek, flera versioner", price: "150 000–250 000 kr", lead: "same-day + 2 veckor" },
        ],
        footnote: "Alla priser exklusive moms. Återkommande event samlas med fördel i ett abonnemang.",
      }}
      sections={[
        localProductionSection("goteborg", "Göteborg"),
        {
          id: "upplagg",
          heading: "Så lägger vi upp en eventdag i Göteborg",
          body: (
            <p>
              Recce av lokalen innan eventet: ljus, ljud, kameraplaceringar och
              intervjuplats. Under dagen filmar vi scen, mingel och detaljer
              medan en redigerare klipper social-materialet på plats. Pauserna
              används till talar- och kundintervjuer som blir egna{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/kundcase-film">
                kundcase
              </a>
              .
            </p>
          ),
        },
        {
          id: "efterat",
          heading: "Vad materialet används till efteråt",
          body: (
            <p>
              Highlight-filmen säljer in nästa edition. Talarklippen blir
              innehåll i 4–8 veckor. B-roll används i kampanjer, rekrytering och
              på sajten. Se även{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/filmproduktion/goteborg">
                filmproduktion i Göteborg
              </a>{" "}
              för övriga format.
            </p>
          ),
        },
        {
          id: "ovriga-orter",
          heading: "Andra orter",
          body: (
            <p>
              Vi filmar event i hela Sverige — se{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/eventfilm/stockholm">
                eventfilm i Stockholm
              </a>{" "}
              eller{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/eventfilm">
                eventfilm generellt
              </a>
              .
            </p>
          ),
        },
      ]}
      faq={FAQ}
      caseLimit={3}
      riskReducers
      closing={{
        title: "Event i Göteborg på gång?",
        body: "Boka 30 minuter så planerar vi täckning, leverans och pris.",
        primary: { label: "Boka möte", to: "/kontakt" },
        secondary: { label: "Se priser", to: "/priser" },
      }}
    />
  );
}
